/**
 * Reusable hook for game session lifecycle.
 *
 * Wraps the game API endpoints: start, content-sets, content, item-attempt, end.
 * Any React-based game can use this instead of the iframe postMessage SDK.
 */

import { useState, useCallback, useRef } from 'react'
import { api } from '@/lib/api'
import type {
  GameSessionData,
  ContentSet,
  MasteryItem,
  ContentStats,
  MasteryUpdate,
  GameEndResult,
} from '@/components/fact-strike/types'

interface UseGameSessionReturn {
  session: GameSessionData | null
  contentSets: ContentSet[]
  items: MasteryItem[]
  stats: ContentStats | null
  isLoading: boolean
  error: string | null

  startSession: (mode?: string) => Promise<GameSessionData>
  fetchContentSets: (category?: string) => Promise<ContentSet[]>
  fetchItems: (contentSetId: number, count?: number) => Promise<MasteryItem[]>
  recordAttempt: (
    itemId: number,
    studentAnswer: string,
    isCorrect: boolean,
    responseTimeMs: number,
  ) => Promise<MasteryUpdate>
  endSession: (finalScore: number, stats?: Record<string, unknown>) => Promise<GameEndResult>
  abandonSession: () => void
}

export function useGameSession(gameId: string): UseGameSessionReturn {
  const [session, setSession] = useState<GameSessionData | null>(null)
  const [contentSets, setContentSets] = useState<ContentSet[]>([])
  const [items, setItems] = useState<MasteryItem[]>([])
  const [stats, setStats] = useState<ContentStats | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Keep a ref for abandon on unmount
  const sessionRef = useRef<GameSessionData | null>(null)

  const startSession = useCallback(async (mode = 'solo'): Promise<GameSessionData> => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await api.post(`/api/games/${gameId}/start`, { mode })
      const data: GameSessionData = {
        sessionId: res.data.session_id,
        gameId: res.data.game_id,
        mode: res.data.mode,
        player: {
          id: res.data.player.id,
          studentId: res.data.player.student_id,
          parentId: res.data.player.parent_id,
          name: res.data.player.name,
          level: res.data.player.level,
          title: res.data.player.title,
        },
        gameConfig: {
          xpPerCorrect: res.data.game_config.xp_per_correct,
          xpCompletionBonus: res.data.game_config.xp_completion_bonus,
        },
      }
      setSession(data)
      sessionRef.current = data
      return data
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start session'
      setError(msg)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [gameId])

  const fetchContentSets = useCallback(async (category?: string): Promise<ContentSet[]> => {
    setIsLoading(true)
    setError(null)
    try {
      const params = category ? `?category=${category}` : ''
      const res = await api.get(`/api/games/${gameId}/content-sets${params}`)
      const sets: ContentSet[] = (res.data.contentSets ?? []).map((s: Record<string, unknown>) => ({
        id: s.id,
        name: s.name,
        category: s.category,
        description: s.description,
        itemCount: s.item_count ?? s.itemCount,
        gradeLevel: s.grade_level ?? s.gradeLevel,
        difficulty: s.difficulty,
        extraData: s.extra_data ?? s.extraData,
        focus: (s.focus ?? null) as ContentSet['focus'],
      }))
      setContentSets(sets)
      return sets
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch content sets'
      setError(msg)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [gameId])

  const fetchItems = useCallback(async (
    contentSetId: number,
    count = 15,
  ): Promise<MasteryItem[]> => {
    if (!session) throw new Error('Session not started')
    setIsLoading(true)
    setError(null)
    try {
      const res = await api.post(`/api/games/session/${session.sessionId}/content`, {
        contentSetId,
        count,
        includeNew: true,
        newItemLimit: 5,
      })
      const fetched: MasteryItem[] = (res.data.items ?? []).map((i: Record<string, unknown>) => ({
        id: i.id,
        itemKey: i.item_key ?? i.itemKey,
        itemType: i.item_type ?? i.itemType,
        prompt: i.prompt,
        correctAnswer: i.correct_answer ?? i.correctAnswer,
        extraData: i.extra_data ?? i.extraData,
        difficultyRating: i.difficulty_rating ?? i.difficultyRating,
        tags: i.tags,
        masteryLevel: i.mastery_level ?? i.masteryLevel,
        isNew: i.is_new ?? i.isNew,
        isLapsed: i.is_lapsed ?? i.isLapsed,
        isDueForReview: i.is_due_for_review ?? i.isDueForReview,
      }))
      setItems(fetched)
      if (res.data.stats) {
        setStats({
          totalItems: res.data.stats.total_items ?? res.data.stats.totalItems ?? 0,
          masteredCount: res.data.stats.mastered_count ?? res.data.stats.masteredCount ?? 0,
          practicedCount: res.data.stats.practiced_count ?? res.data.stats.practicedCount ?? 0,
          learningCount: res.data.stats.learning_count ?? res.data.stats.learningCount ?? 0,
          newCount: res.data.stats.new_count ?? res.data.stats.newCount ?? 0,
          lapsedCount: res.data.stats.lapsed_count ?? res.data.stats.lapsedCount ?? 0,
          dueForReviewCount: res.data.stats.due_for_review_count ?? res.data.stats.dueForReviewCount ?? 0,
          masteryPercentage: res.data.stats.mastery_percentage ?? res.data.stats.masteryPercentage ?? 0,
        })
      }
      return fetched
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch items'
      setError(msg)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [session])

  const recordAttempt = useCallback(async (
    itemId: number,
    studentAnswer: string,
    isCorrect: boolean,
    responseTimeMs: number,
  ): Promise<MasteryUpdate> => {
    if (!session) throw new Error('Session not started')
    try {
      const res = await api.post(`/api/games/session/${session.sessionId}/item-attempt`, {
        itemId,
        studentAnswer,
        isCorrect,
        responseTimeMs,
      })
      return {
        itemId,
        previousLevel: res.data.previous_level ?? res.data.previousLevel ?? 0,
        newLevel: res.data.new_level ?? res.data.newLevel ?? 0,
        qualityRating: res.data.quality_rating ?? res.data.qualityRating ?? 0,
        isNew: res.data.is_new ?? false,
        isLapsed: res.data.is_lapsed ?? false,
        nextReviewAt: res.data.next_review_at,
      }
    } catch (err: unknown) {
      console.error('Failed to record attempt:', err)
      // Return a fallback so the game doesn't crash
      return {
        itemId,
        previousLevel: 0,
        newLevel: 0,
        qualityRating: isCorrect ? 3 : 0,
        isNew: false,
        isLapsed: false,
      }
    }
  }, [session])

  const endSession = useCallback(async (
    finalScore: number,
    extraStats: Record<string, unknown> = {},
  ): Promise<GameEndResult> => {
    if (!session) throw new Error('Session not started')
    setIsLoading(true)
    try {
      const res = await api.post(`/api/games/session/${session.sessionId}/end`, {
        finalScore,
        stats: extraStats,
      })
      const result: GameEndResult = {
        xpEarned: res.data.xp_earned ?? res.data.xpEarned ?? 0,
        accuracy: res.data.accuracy ?? 0,
        duration: res.data.duration_seconds ?? res.data.duration ?? 0,
        questionsTotal: res.data.questions_total ?? res.data.questionsTotal ?? 0,
        questionsCorrect: res.data.questions_correct ?? res.data.questionsCorrect ?? 0,
        playerTotalXp: res.data.player_total_xp ?? 0,
        playerLevel: res.data.player_level ?? 1,
        playerTitle: res.data.player_title ?? 'Explorer',
      }
      sessionRef.current = null
      return result
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to end session'
      setError(msg)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [session])

  const abandonSession = useCallback(() => {
    const s = sessionRef.current
    if (s) {
      // Fire-and-forget via sendBeacon for reliability on tab close
      const url = `/api/games/session/${s.sessionId}/abandon`
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url)
      } else {
        fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
      }
      sessionRef.current = null
    }
  }, [])

  return {
    session,
    contentSets,
    items,
    stats,
    isLoading,
    error,
    startSession,
    fetchContentSets,
    fetchItems,
    recordAttempt,
    endSession,
    abandonSession,
  }
}
